/**
 * LodShellSelector — pure geometry for the LOD "onion": which chunk positions are in range at each
 * LOD level around a center point.
 *
 * Port of worldify-app's ChunkCoordinator.getNextChunksInRangeAndNeighbours shell builder:
 *  - The BASE level is a Chebyshev cube of half-extent `baseRadius` (matching the visibility BFS's
 *    cube metric), OCTET-ROUNDED: every member is mapped to its parent and every parent expanded
 *    back to all 8 children.
 *  - Each coarser level L is the parents of level L-1, dilated by one chunk (3³ neighbourhood),
 *    octet-rounded again. Each level therefore fully COVERS the finer level below it plus a ring —
 *    interior coarse chunks are the swap fallback (drawn until their children mesh), the ring is
 *    the extra view distance.
 *
 * THE OCTET INVARIANT (the no-gap/no-overlap mechanism): every level's set is a union of complete
 * sibling octets, so a parent's 8 children are either ALL in range or NONE are. The top-down
 * visibility walk (LodResidencyManager) then partitions the covered volume exactly — a partial
 * octet, which would force a parent/child overlap or a hole, cannot exist.
 *
 * Purely geometric: no terrain, BFS, or residency knowledge (vertical clipping and occlusion are
 * applied by the consumer). Zero deps → unit-testable like VisibilityBFS.
 */

import {
  CHUNK_WORLD_SIZE,
  MAX_PACKABLE_LOD,
  packChunkKey as packCoord,
  unpackChunkKeyTuple as unpackCoord,
  packColumn,
} from '@worldify/shared';

// ============== Packed coordinates ==============
// ONE packing home: shared/src/util/packedKey.ts. Re-exported here under the client's established
// names — the 60+ voxel-side call sites read packCoord/unpackCoord/packColumn from this module.
// MAX_PACKABLE_LOD rides along because the envelope below derives from it.
export { packCoord, unpackCoord, packColumn, MAX_PACKABLE_LOD };

/** The lod a `packColumn` key carries — its low field. Kept beside packColumn so the layout is
 *  known in one place; callers counting per-level in-flight columns need it. */
export const columnKeyLod = (colKey: number): number => colKey % (MAX_PACKABLE_LOD + 1);

// ============== THE LOD ENVELOPE (one place, because three files used to guess it) ==============
/**
 * Zoom-out ceiling: how coarse the CENTRAL region may get in explore (each step doubles the orbit
 * range — see ExploreCamera.setExploreMaxBaseLod). First person pins 0.
 */
export const MAX_BASE_LOD = 3;
/**
 * Deepest far-view ring count the engine supports = whatever is left of the packing budget once the
 * zoom-out ceiling has taken its share, since maxLod = displayBaseLod + farLodLevels. The UI's Far View
 * options are checked against this (QualityPresets), so adding an option that the packing cannot carry
 * fails loudly at module load instead of corrupting column probes at lod 8.
 */
export const MAX_FAR_LOD_LEVELS = MAX_PACKABLE_LOD - MAX_BASE_LOD;

/**
 * How far the onion reaches from its centre, in metres, at a given depth — the number the camera's far
 * plane has to clear or the deepest rings are generated and then clipped away unseen. Each level covers
 * the level below plus a one-chunk ring, so the extent is the base cube's half-extent (+ that ring, +
 * the octet rounding) scaled by 2^lod.
 */
export function lodViewReachMetres(baseRadius: number, maxLod: number): number {
  return CHUNK_WORLD_SIZE * (baseRadius + 2) * (1 << maxLod);
}

// ============== Parent / child relations ==============

/** Parent coordinate one level coarser: floor(c/2) per axis (>> works as floor for negatives). */
export function parentOf(cx: number, cy: number, cz: number): [number, number, number] {
  return [cx >> 1, cy >> 1, cz >> 1];
}

/** The 8 children one level finer: c*2 + octant bit per axis. */
export function childrenOf(cx: number, cy: number, cz: number): Array<[number, number, number]> {
  const bx = cx << 1, by = cy << 1, bz = cz << 1;
  const out: Array<[number, number, number]> = [];
  for (let i = 0; i < 8; i++) {
    out.push([bx + (i & 1), by + ((i >> 1) & 1), bz + ((i >> 2) & 1)]);
  }
  return out;
}

/**
 * Octet-align one axis of the interval [center−radius, center+radius]: min floored to EVEN, max
 * raised to ODD, so the interval is a union of complete sibling pairs (its 3D product is a union
 * of complete octets). This is what the visibility BFS cut and the base coverage test use — an
 * UNALIGNED cut leaves frontier octets with a mix of wanted and distance-culled children, forcing
 * the LOD-1 parent to draw over already-meshed fine chunks (the bald-band bug). Bitwise & | are
 * two's-complement, so the rounding directions hold for negative coordinates too.
 */
export function octetAlignedBounds(center: number, radius: number): [number, number] {
  return [(center - radius) & ~1, (center + radius) | 1];
}

// ============== Shell computation ==============

export interface LodShellInput {
  /** Streaming center in world metres. */
  centerWorld: { x: number; y: number; z: number };
  /** LOD level of the base (finest displayed) region. */
  baseLod: number;
  /** Chebyshev half-extent of the base region, in base-lod chunks (= visibilityRadius). */
  baseRadius: number;
  /** Number of coarser levels above the base (the "far view rings" quality setting). */
  farLevels: number;
}

export interface LodLevelSet {
  lod: number;
  /** In-range chunk positions at this level, packed with packCoord. */
  inRange: Set<number>;
}

export interface LodShellResult {
  /** One entry per level, finest (baseLod) first. */
  levels: LodLevelSet[];
  maxLod: number;
}

/** Octet-round a set: map every member to its parent, then expand each parent to all 8 children. */
function octetRound(set: Set<number>): Set<number> {
  const parents = new Set<number>();
  for (const p of set) {
    const [x, y, z] = unpackCoord(p);
    parents.add(packCoord(x >> 1, y >> 1, z >> 1));
  }
  const out = new Set<number>();
  for (const p of parents) {
    const [px, py, pz] = unpackCoord(p);
    const bx = px << 1, by = py << 1, bz = pz << 1;
    for (let i = 0; i < 8; i++) {
      out.add(packCoord(bx + (i & 1), by + ((i >> 1) & 1), bz + ((i >> 2) & 1)));
    }
  }
  return out;
}

/**
 * Compute the in-range chunk set per LOD level around the center.
 *
 * Levels are the base region (cube, octet-rounded) plus `farLevels` coarser coverings, each the
 * 1-dilated parents of the level below, octet-rounded. Every level fully covers the level below.
 */
export function computeLodShells(input: LodShellInput): LodShellResult {
  const { centerWorld, baseLod, baseRadius, farLevels } = input;

  const baseChunkSize = CHUNK_WORLD_SIZE * (1 << baseLod);
  const ccx = Math.floor(centerWorld.x / baseChunkSize);
  const ccy = Math.floor(centerWorld.y / baseChunkSize);
  const ccz = Math.floor(centerWorld.z / baseChunkSize);

  // Base cube (Chebyshev half-extent baseRadius), octet-rounded.
  let cur = new Set<number>();
  for (let dz = -baseRadius; dz <= baseRadius; dz++) {
    for (let dy = -baseRadius; dy <= baseRadius; dy++) {
      for (let dx = -baseRadius; dx <= baseRadius; dx++) {
        cur.add(packCoord(ccx + dx, ccy + dy, ccz + dz));
      }
    }
  }
  cur = octetRound(cur);

  const levels: LodLevelSet[] = [{ lod: baseLod, inRange: cur }];

  for (let lod = baseLod + 1; lod <= baseLod + farLevels; lod++) {
    // Parents of the finer level.
    const parents = new Set<number>();
    for (const p of levels[levels.length - 1].inRange) {
      const [x, y, z] = unpackCoord(p);
      parents.add(packCoord(x >> 1, y >> 1, z >> 1));
    }
    // Dilate by one chunk in every direction (26-neighbourhood + self) → the ring.
    const dilated = new Set<number>();
    for (const p of parents) {
      const [x, y, z] = unpackCoord(p);
      for (let dz = -1; dz <= 1; dz++) {
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            dilated.add(packCoord(x + dx, y + dy, z + dz));
          }
        }
      }
    }
    const rounded = octetRound(dilated);
    levels.push({ lod, inRange: rounded });
  }

  return { levels, maxLod: baseLod + farLevels };
}
